import type { ReactNode } from "react";
import type { AccessLevel } from "../portal-auth";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { getPortalUrl } from "../lib/portal-url";
import { DevAccessPill } from "./DevAccessPill";
import logoMark from "../assets/brand/logo-mark.png";
import "./layout.css";

export interface ToolLayoutProps {
  toolName: string;
  access: AccessLevel | null;
  userName: string | null;
  loading?: boolean;
  error?: string | null;
  portalUrl?: string;
  onOpenSettings?: () => void;
  onShowOnboarding?: () => void;
  /** Opslaan / openen-knoppen in de header (bijv. via ToolFileActions). */
  fileActions?: ReactNode;
  children: ReactNode;
}

function accessBadgeClass(access: AccessLevel | null): string {
  if (access === "PAID") return "jari-badge jari-badge--paid";
  if (access === "FREE") return "jari-badge jari-badge--free";
  return "jari-badge jari-badge--none";
}

export function ToolLayout({
  toolName,
  access,
  userName,
  loading,
  error,
  portalUrl,
  onOpenSettings,
  onShowOnboarding,
  fileActions,
  children,
}: ToolLayoutProps) {
  const portal = getPortalUrl(portalUrl);
  useDocumentTitle(toolName);

  return (
    <div className="jari-tool-layout">
      <header className="jari-tool-header">
        <div className="jari-tool-header__brand">
          <a
            href={portal}
            className="jari-tool-header__logo-link"
            target="_blank"
            rel="noopener noreferrer"
            title="Interact GGZ portal"
          >
            <img src={logoMark} alt="" className="jari-tool-header__logo" aria-hidden="true" />
          </a>
          <div>
            <h1 className="jari-tool-header__title">{toolName}</h1>
            {userName && !loading && (
              <p className="jari-tool-header__user">{userName}</p>
            )}
          </div>
          {!loading && access && (
            <span className={accessBadgeClass(access)}>{access}</span>
          )}
        </div>
        <div className="jari-tool-header__end">
          {fileActions && (
            <div className="jari-tool-header__file">{fileActions}</div>
          )}
          <div className="jari-tool-header__actions">
            {onShowOnboarding && (
              <button
                type="button"
                className="jari-btn jari-btn-icon"
                onClick={onShowOnboarding}
                title="Uitleg tonen"
                aria-label="Uitleg tonen"
              >
                ?
              </button>
            )}
            {onOpenSettings && (
              <button
                type="button"
                className="jari-btn jari-btn-icon"
                onClick={onOpenSettings}
                title="Instellingen"
                aria-label="Instellingen"
              >
                ⚙
              </button>
            )}
            <a
              href={portal}
              className="jari-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Portal
            </a>
          </div>
        </div>
      </header>

      <main className="jari-tool-main">
        {loading && (
          <div className="jari-tool-auth-banner jari-tool-auth-banner--loading">
            Portal-sessie controleren…
          </div>
        )}
        {error && !loading && (
          <div className="jari-tool-auth-banner jari-tool-auth-banner--error">
            {error}{" "}
            <a href={portal}>Naar het portal</a>
          </div>
        )}
        {children}
      </main>

      {import.meta.env.DEV && <DevAccessPill />}
    </div>
  );
}
